import { gql, useQuery } from '@apollo/client';
import { Skeleton } from '@perch-labs/shrike';
import { ErrorMessage } from '~/components/error-message';
import { Container } from '~/components/layout';
import { OnChange } from '~/components/validating-input';
import { PERCH_CREDIT_CHECK_CONSENT_KEY } from '~/utils/constants';
import { GeneralTermsAndCreditCheck, PhoneConsent } from './perch-consent-components';
import { FETCH_PERCH_CREDIT_CHECK_CONSENT } from './perch-credit-check-consent.queries';
import { getConsentMap } from './perch-general-terms.utils';
import { PerchGeneralContextWrapper, PerchPhoneConsentWrapper } from './perch-general.style';

export const FETCH_PRECH_GENERAL_TERMS = gql`
  query Flex_FetchPerchGeneralTerms {
    generalContractVersions(contractType: [privacy, terms], owner: Perch) {
      id
      displayName
    }
  }
`;

export const FETCH_PERCH_CONSENT = gql`
  query Flex_FetchPerchConsent($signupContractTemplateId: Int!) {
    signupContractTemplateContractVersions(
      contractTypes: [consent]
      signupContractTemplateId: $signupContractTemplateId
    ) {
      id
      displayName
      internalName
      contractType
      notes
      fileUrl
    }
  }
`;

type GeneralProps = {
  onChange: OnChange<number[]>;
  requireFico: boolean;
  customerType: string;
  perchSfProductId?: string;
  showPhoneConsent: boolean;
  signupContractTemplateId?: number;
};

export const PerchGeneralTermsAndConsent = ({
  onChange,
  requireFico,
  customerType,
  perchSfProductId,
  showPhoneConsent,
  signupContractTemplateId,
}: GeneralProps) => {
  const variables: Omit<GeneralProps, 'onChange' | 'showPhoneConsent'> = {
    requireFico,
    customerType,
  };
  if (perchSfProductId) {
    variables.perchSfProductId = perchSfProductId;
  }
  if (signupContractTemplateId) {
    variables.signupContractTemplateId = signupContractTemplateId;
  }
  const { data, loading, error } = useQuery(FETCH_PRECH_GENERAL_TERMS);
  const {
    data: consentData,
    loading: consentLoading,
    error: consentError,
  } = useQuery(FETCH_PERCH_CONSENT, {
    variables: { signupContractTemplateId },
  });
  const {
    data: creditCheckData,
    loading: creditCheckLoading,
    error: creditCheckError,
  } = useQuery(FETCH_PERCH_CREDIT_CHECK_CONSENT, {
    skip: !requireFico,
  });
  const contracts = data?.generalContractVersions || [];
  const consents = getConsentMap([
    ...(consentData?.signupContractTemplateContractVersions || []),
    ...(creditCheckData?.currentContractVersions || []).filter(
      consent => consent.internalName === PERCH_CREDIT_CHECK_CONSENT_KEY,
    ),
  ]);
  const contractIds = contracts.map(contract => contract.id);

  if (error || consentError || creditCheckError) {
    return (
      <ErrorMessage
        marginTop={16}
        allow
        error="There was a problem loading our terms. Please refresh the page to try again."
      />
    );
  } else if (loading || consentLoading || creditCheckLoading) {
    return (
      <Container>
        <Skeleton backgroundColor="tertiary" width="100%" />
        <Skeleton backgroundColor="tertiary" width="100%" />
      </Container>
    );
  }

  return (
    <>
      <PerchPhoneConsentWrapper>
        {showPhoneConsent ? <PhoneConsent onChange={onChange} consents={consents} /> : null}
      </PerchPhoneConsentWrapper>
      <PerchGeneralContextWrapper>
        <GeneralTermsAndCreditCheck
          onChange={onChange}
          contractIds={contractIds}
          contracts={contracts}
          requireFico={requireFico}
          consents={consents}
        />
      </PerchGeneralContextWrapper>
    </>
  );
};
